import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Search, Loader2, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { getPagesContingency, type ContingencyPage } from '@/services/api/connections';

/**
 * Contingência de apps por página.
 *
 * O erro 613 do Meta é limite POR APP (~12k requisições/hora medidas em
 * produção). Quando várias páginas grandes dividem o mesmo app, o 613 deixa
 * de ser acaso e vira matemática — foi o que o levantamento de 04/09
 * encontrou: 9 de 17 apps acima de 100% da capacidade.
 *
 * Esta tela mostra onde diluir a carga ANTES de escalar a captação.
 */

type Filtro = 'criticas' | 'atencao' | 'todas';

const PRIORIDADE_LABEL: Record<string, string> = {
    critica: 'Crítica',
    atencao: 'Atenção',
    ok: 'OK',
    sem_app: 'Sem app',
};

const PRIORIDADE_CLASSE: Record<string, string> = {
    critica: 'bg-red-50 text-red-700 border-red-200',
    atencao: 'bg-amber-50 text-amber-700 border-amber-200',
    ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    sem_app: 'bg-gray-100 text-gray-600 border-gray-200',
};

function barraUso(pct: number) {
    if (pct >= 100) return 'bg-red-500';
    if (pct >= 70) return 'bg-amber-500';
    return 'bg-emerald-500';
}

export default function ContingencyPanel() {
    const [filtro, setFiltro] = useState<Filtro>('criticas');
    const [busca, setBusca] = useState('');
    const [janela, setJanela] = useState(7);

    const { data, isLoading, isFetching, refetch } = useQuery({
        queryKey: ['pages-contingency', janela],
        queryFn: () => getPagesContingency(janela),
        staleTime: 1000 * 60 * 5,
    });

    const paginas = useMemo(() => {
        const todas = data?.pages ?? [];
        const porFiltro = todas.filter((p) => {
            if (filtro === 'criticas') return p.priority === 'critica' || p.priority === 'sem_app';
            if (filtro === 'atencao') return p.priority === 'atencao';
            return true;
        });
        const termo = busca.trim().toLowerCase();
        if (!termo) return porFiltro;
        return porFiltro.filter(
            (p) => p.page_name.toLowerCase().includes(termo) || p.page_id.includes(termo)
        );
    }, [data, filtro, busca]);

    if (isLoading) {
        return (
            <div className="rounded-2xl bg-white p-10 shadow-sm border border-gray-100 flex items-center justify-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">Calculando carga por app…</span>
            </div>
        );
    }

    const k = data?.kpis;
    const capacidade = data?.config.app_capacity_hour ?? 12000;

    return (
        <div className="space-y-5">
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Apps saturados</p>
                    <p className="mt-1 text-2xl font-bold text-red-600 tabular-nums">
                        {k?.apps_saturated ?? 0}
                        <span className="text-base font-medium text-gray-400"> / {k?.apps_total ?? 0}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        hoje · com régua de {k?.window_days ?? janela}d:{' '}
                        <span className="font-semibold text-emerald-600">
                            {k?.apps_saturated_after_window ?? 0}
                        </span>
                    </p>
                </div>
                <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Páginas críticas</p>
                    <p className="mt-1 text-2xl font-bold text-amber-600 tabular-nums">{k?.pages_critical ?? 0}</p>
                    <p className="text-xs text-gray-500 mt-0.5">rodando em app saturado</p>
                </div>
                <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Inscrições faltando</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">{k?.subscriptions_missing ?? 0}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{k?.pages_single_app ?? 0} páginas com 1 app só</p>
                </div>
                <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">Envio em risco</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">
                        {(k?.queue_at_risk ?? 0).toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        de {(k?.queue_total ?? 0).toLocaleString('pt-BR')} hoje · com régua:{' '}
                        <span className="font-semibold text-emerald-600">
                            {(k?.projected_queue_at_risk ?? 0).toLocaleString('pt-BR')}
                        </span>
                    </p>
                </div>
            </div>

            {/* Apps */}
            <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold text-gray-900">Carga por app</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Envio de <strong>hoje</strong> (leads ativos) contra a capacidade medida de{' '}
                            {capacidade.toLocaleString('pt-BR')} requisições/hora por app
                        </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
                        <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
                <div className="divide-y divide-gray-50">
                    {(data?.apps ?? []).map((app) => (
                        <div key={app.app_id} className="px-5 py-2.5 flex items-center gap-4">
                            <div className="w-52 flex-shrink-0 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate" title={app.app_name}>
                                    {app.app_name}
                                </div>
                                <div className="text-xs text-gray-400 font-mono truncate">
                                    #{app.app_id}
                                    {app.meta_app_id && <span className="ml-1.5">· {app.meta_app_id}</span>}
                                </div>
                            </div>
                            <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full ${barraUso(app.usage_pct)}`}
                                    style={{ width: `${Math.min(100, app.usage_pct)}%` }}
                                />
                            </div>
                            <div className="w-24 text-right flex-shrink-0">
                                <span
                                    className={`text-sm font-semibold tabular-nums ${
                                        app.usage_pct >= 100 ? 'text-red-600' : app.usage_pct >= 70 ? 'text-amber-600' : 'text-gray-600'
                                    }`}
                                >
                                    {app.usage_pct}%
                                </span>
                                <div
                                    className="text-[10px] text-gray-400 tabular-nums"
                                    title={`Com a régua de engajamento a carga cai para ${app.load_projected.toLocaleString('pt-BR')} leads`}
                                >
                                    régua: {app.usage_pct_projected}%
                                </div>
                            </div>
                            <span className="text-xs text-gray-400 w-16 text-right">{app.pages} pgs</span>
                            <span className="hidden xl:block text-xs text-gray-500 truncate w-56">
                                {app.top_pages.map((p) => `${p.page_name} (${p.queue})`).join(' · ')}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Páginas */}
            <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-3 justify-between">
                    <div>
                        <h3 className="font-semibold text-gray-900">Páginas para diluir</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Fila estimada com janela de {data?.config.window_days ?? janela} dias
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                            {([
                                { id: 'criticas', label: 'Críticas' },
                                { id: 'atencao', label: 'Atenção' },
                                { id: 'todas', label: 'Todas' },
                            ] as { id: Filtro; label: string }[]).map((f) => (
                                <button
                                    key={f.id}
                                    onClick={() => setFiltro(f.id)}
                                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                                        filtro === f.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <select
                            value={janela}
                            onChange={(e) => setJanela(Number(e.target.value))}
                            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
                        >
                            {[7, 14, 21, 30].map((d) => (
                                <option key={d} value={d}>{d} dias</option>
                            ))}
                        </select>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <Input
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                placeholder="Buscar página"
                                className="pl-8 h-8 text-xs w-44"
                            />
                        </div>
                    </div>
                </div>

                {paginas.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-gray-500">
                        Nenhuma página neste filtro.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                                    <th className="px-5 py-2 font-medium">Página</th>
                                    <th className="px-3 py-2 font-medium text-right">Envio hoje</th>
                                    <th className="px-3 py-2 font-medium text-right">Com régua</th>
                                    <th className="px-3 py-2 font-medium">Apps</th>
                                    <th className="px-3 py-2 font-medium text-right">Pior app</th>
                                    <th className="px-3 py-2 font-medium text-right">Faltam</th>
                                    <th className="px-3 py-2 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {paginas.map((p: ContingencyPage) => (
                                    <tr key={p.page_id} className="hover:bg-gray-50/60">
                                        <td className="px-5 py-2.5">
                                            <div className="font-medium text-gray-900 truncate max-w-[220px]">{p.page_name}</div>
                                            <div className="text-xs text-gray-400 font-mono">{p.page_id}</div>
                                        </td>
                                        <td className="px-3 py-2.5 text-right tabular-nums font-medium">
                                            {p.active_leads.toLocaleString('pt-BR')}
                                        </td>
                                        <td
                                            className="px-3 py-2.5 text-right tabular-nums text-emerald-600"
                                            title="Quantos leads restariam com a régua de engajamento aplicada"
                                        >
                                            {p.queue.toLocaleString('pt-BR')}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-gray-600">
                                            {p.apps_detail?.length ? (
                                                <div className="flex flex-col gap-0.5">
                                                    {p.apps_detail.map((a) => (
                                                        <span
                                                            key={a.id}
                                                            className="truncate max-w-[190px]"
                                                            title={`${a.name} · interno #${a.id}${a.meta_app_id ? ` · Meta ${a.meta_app_id}` : ''}`}
                                                        >
                                                            <span className={a.usage_pct >= 100 ? 'text-red-600 font-medium' : ''}>
                                                                {a.name}
                                                            </span>
                                                            <span className="text-gray-400 font-mono ml-1">#{a.id}</span>
                                                        </span>
                                                    ))}
                                                    {p.single_app_lock && (
                                                        <span
                                                            className="text-[10px] font-semibold text-red-600 uppercase tracking-wide"
                                                            title="Só existe este token vivo para a página — se ele saturar, não há candidato de fallback (o desvio automático do orquestrador exige um segundo token na própria página)"
                                                        >
                                                            sem fallback
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                            <span
                                                className={`tabular-nums font-semibold ${
                                                    p.worst_app_usage >= 100 ? 'text-red-600' : p.worst_app_usage >= 70 ? 'text-amber-600' : 'text-gray-600'
                                                }`}
                                            >
                                                {p.worst_app_usage}%
                                            </span>
                                            {p.worst_app_name && (
                                                <div className="text-xs text-gray-400 truncate max-w-[140px] ml-auto" title={p.worst_app_name}>
                                                    {p.worst_app_name}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-right tabular-nums">
                                            {p.apps_missing > 0 ? (
                                                <span className="font-semibold text-blue-600">+{p.apps_missing}</span>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span
                                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${
                                                    PRIORIDADE_CLASSE[p.priority] ?? PRIORIDADE_CLASSE.ok
                                                }`}
                                            >
                                                {PRIORIDADE_LABEL[p.priority] ?? p.priority}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="flex gap-2 items-start text-xs text-gray-500 bg-amber-50/60 border border-amber-100 rounded-xl px-4 py-3">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p>
                    A carga de cada app soma a fila das páginas que ele atende — quando uma página tem mais de um app,
                    a fila é dividida entre eles. O desvio automático do orquestrador só existe quando a própria página
                    tem um <strong>segundo token vivo</strong>: página marcada <strong>"sem fallback"</strong> tem 1 único
                    app — se ele saturar, não há para onde desviar, mesmo que outros apps do pool geral estejam ociosos.
                    A correção é inscrever a página (gerar token) em outro app com folga.
                </p>
            </div>
        </div>
    );
}
